import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Calendar, Clock, User, Video, MapPin, X, Loader2, CheckCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  pending: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700' },
  confirmed: { label: 'Confirmado', color: 'bg-green-100 text-green-700' },
  canceled: { label: 'Cancelado', color: 'bg-red-100 text-red-700' },
  completed: { label: 'Concluído', color: 'bg-blue-100 text-blue-700' },
};

const MODALITY_ICONS = {
  online: Video,
  presencial: MapPin,
  hibrido: Calendar,
};

export default function BookingsPage() {
  const [professionalBookings, setProfessionalBookings] = useState([]);
  const [clientBookings, setClientBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('professional');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { loadBookings(); }, []);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const profRes = await axios.get(`${API}/api/professionals/me/bookings`, { headers }).catch(() => ({ data: [] }));
      setProfessionalBookings(Array.isArray(profRes.data) ? profRes.data : profRes.data.bookings || []);
      setClientBookings([]);
    } catch (err) {
      console.error('Error loading bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  const cancelBooking = async (bookingId) => {
    if (!window.confirm('Tem certeza que deseja cancelar este agendamento?')) return;
    try {
      await axios.post(`${API}/api/professionals/bookings/${bookingId}/cancel`, {}, { headers });
      toast.success('Agendamento cancelado');
      loadBookings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao cancelar');
    }
  };

  const BookingCard = ({ booking, isProfessional }) => {
    const status = STATUS_MAP[booking.status] || STATUS_MAP.pending;
    const ModalityIcon = MODALITY_ICONS[booking.modality] || Calendar;
    const startsAt = new Date(booking.starts_at);

    return (
      <Card className="mb-3" data-testid={`booking-${booking.id}`}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium text-sm">
                  {isProfessional ? booking.user_name : booking.professional_name}
                </span>
                <Badge className={`${status.color} text-xs`}>{status.label}</Badge>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  {startsAt.toLocaleDateString('pt-BR')}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" />
                  {startsAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                </span>
                <span className="flex items-center gap-1">
                  <ModalityIcon className="h-3.5 w-3.5" />
                  {booking.modality}
                </span>
                <span>{booking.length_min} min</span>
              </div>
              {booking.notes && (
                <p className="text-xs text-muted-foreground mt-2 italic">"{booking.notes}"</p>
              )}
            </div>
            {booking.status === 'pending' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => cancelBooking(booking.id)}
                className="text-red-500 hover:bg-red-50"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="bookings-page">
      <div>
        <h1 className="text-2xl font-bold">Meus Agendamentos</h1>
        <p className="text-muted-foreground">Gerencie suas sessões e consultas</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="professional">
            Como Profissional ({professionalBookings.length})
          </TabsTrigger>
          <TabsTrigger value="client">
            Como Cliente ({clientBookings.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="professional" className="mt-4">
          {professionalBookings.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">Nenhum agendamento como profissional</p>
              </CardContent>
            </Card>
          ) : (
            professionalBookings.map(b => (
              <BookingCard key={b.id} booking={b} isProfessional />
            ))
          )}
        </TabsContent>

        <TabsContent value="client" className="mt-4">
          {clientBookings.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <CheckCircle className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">Nenhum agendamento como cliente</p>
              </CardContent>
            </Card>
          ) : (
            clientBookings.map(b => (
              <BookingCard key={b.id} booking={b} isProfessional={false} />
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
